import { LogOut, User as UserIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useLogout } from "@profitlens/api-client";

import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@profitlens/ui/dropdown-menu";
import { toast } from "@profitlens/ui/toast";
import { useSession } from "@/features/auth/use-session";
import { isApiError } from "@profitlens/api-client";
import { useNavigate } from "react-router";

import { useAuthStore } from "@/stores/auth-store";

export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSession();
  const logout = useLogout();
  const expire = useAuthStore((state) => state.expire);

  const email = user?.email ?? "";
  const initial = email.slice(0, 1).toUpperCase() || "U";

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
    } catch (error) {
      toast({
        title: t("common.error"),
        description: isApiError(error) ? error.message : undefined,
        variant: "error",
      });
    } finally {
      expire();
      navigate("/login", { replace: true });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label={t("nav.account")}>
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand">
            {initial}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span className="truncate text-sm font-medium text-foreground">{email || "—"}</span>
          {user ? <Badge variant="secondary">{t(`roles.${user.role}`)}</Badge> : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/app/settings")}>
          <UserIcon className="h-4 w-4" />
          {t("settings.title")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void handleLogout()}>
          <LogOut className="h-4 w-4" />
          {t("auth.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
